import { useQuery } from '@tanstack/react-query';

import { useHttpClient } from '~/hooks/useHttpClient';
import { useLocale } from '~/hooks/useLocale';
import { determineRetryAndMaybeLog } from '~/utils/errors';
import { createLogger } from '~/utils/logging';

const log = createLogger('useReviewDayCountsQuery');

export const QUERY_KEY = 'reviewDayCounts';

type GetDayCountsResponse = {
  counts: Record<string, number>;
};

export function useReviewDayCountsQuery({
  periodId,
  year,
  month,
}: {
  periodId: string;
  year: number;
  month: number; // 1-indexed (January = 1)
}) {
  const httpClient = useHttpClient();
  const locale = useLocale();
  const timeZone = locale?.timeZone;

  return useQuery<Record<string, number>>({
    queryKey: [QUERY_KEY, periodId, year, month, timeZone],
    queryFn: async () => {
      log.debug('Fetching review day counts', { periodId, year, month });
      const params = new URLSearchParams({
        periodId,
        year: String(year),
        month: String(month),
      });
      if (timeZone) {
        params.set('tz', timeZone);
      }
      const { counts } = await httpClient.get<GetDayCountsResponse>(`/review/getDayCounts?${params.toString()}`);
      log.info('Fetched review day counts successfully', { count: Object.keys(counts).length });
      return counts;
    },
    retry: (failureCount, error) =>
      determineRetryAndMaybeLog({ operation: 'fetch review day counts', failureCount, error, log }),
    enabled: httpClient.isAuthenticated,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
